import cloneDeep from 'lodash/cloneDeep.js';
import isEmpty from 'lodash/isEmpty.js';

export const defaultMenu = {
    primary: [],
    curriculum: {
        misc_pre: [],
        weeks: { past: [], present: [], future: [] },
        misc_post: [],
    },
    meta: [],
};

export default {
    state: {
        hidden: true,

        // SET_MENU writes into the nested arrays, so a shallow copy would fill defaultMenu itself.
        dynamicMenu: cloneDeep(defaultMenu),

        /** Course id the current dynamicMenu was loaded for; null when unset or after reset. */
        menuCourseId: null,

        /** Course whose menu was requested last. A late response for any other course is dropped. */
        requestedMenuCourseId: null,

        /**
         * The menu request made last. A course can be requested twice, as in A, then B, then A
         * again, so a late response from the first request for A is dropped by this id.
         */
        requestedMenuRequestId: null,

        mobile_nav_open: false,

        staticMenu: null,

        sidebar: null,

        sidebarExpandedRoute: null,
    },

    getters: {
        hasMenu(state) {
            if (state.sidebar) return true;
            if (state.sidebar === 'course-template' && state.dynamicMenu.curriculum.misc_pre) return true;
            if (state.sidebar === 'reports-sidebar') return true;
            if (state.sidebar === 'inbox-sidebar') return true;

            return Object.values(state.dynamicMenu).some(menuSection => {
                if (Array.isArray(menuSection)) {
                    return !isEmpty(menuSection);
                }

                return Object.values(menuSection).some(subSection => !isEmpty(subSection));
            });
        },

        /**
         * Course whose menu was requested but hasn't landed yet, so the sidebar can show a
         * placeholder instead of another course's menu. Null when the shown menu is current.
         */
        pendingMenuCourseId(state) {
            if (state.staticMenu && state.sidebar) {
                return null;
            }

            if (state.requestedMenuCourseId == null || state.requestedMenuCourseId === state.menuCourseId) {
                return null;
            }

            return state.requestedMenuCourseId;
        },

        menu(state) {
            if (state.staticMenu && state.sidebar) {
                return state.staticMenu;
            }

            return state.dynamicMenu;
        },

        flatCourseMenu(state, getters) {
            if (!getters.menu?.curriculum) return [];

            return [
                ...(getters.menu.curriculum.misc_pre || []),
                ...(getters.menu.curriculum.weeks?.past || []),
                ...(getters.menu.curriculum.weeks?.present || []),
                ...(getters.menu.curriculum.weeks?.future || []),
                ...(getters.menu.curriculum.misc_post || []),
            ];
        },

        // Whether the given activity is currently flagged completed in the sidebar menu.
        // Lets callers avoid re-committing MARK_ACTIVITY_COMPLETED for a no-op (already-complete)
        // state, which would otherwise re-fire completion side effects (e.g. the corporate
        // completion redirect) on a passive revisit. Matches the activity by `id` (the menu's
        // activity key, as MARK_ACTIVITY_COMPLETED does) with `activity_id` as a fallback.
        isActivityMarkedCompleteInMenu: (state, getters) => activityId => {
            const target = Number(activityId);
            let located = false;
            let completed = false;

            const walk = items => {
                if (located || !Array.isArray(items)) return;

                for (const item of items) {
                    if (located) return;
                    if (!item || typeof item !== 'object') continue;

                    if (
                        item.type === 'activity' &&
                        (Number(item.id) === target || Number(item.activity_id) === target)
                    ) {
                        located = true;
                        completed = Boolean(item.completed);
                        return;
                    }

                    if (Array.isArray(item.items)) walk(item.items);
                }
            };

            const menu = getters.menu;

            [
                menu?.primary,
                menu?.curriculum?.misc_pre,
                menu?.curriculum?.weeks?.past,
                menu?.curriculum?.weeks?.present,
                menu?.curriculum?.weeks?.future,
                menu?.curriculum?.misc_post,
                menu?.meta,
            ].forEach(walk);

            return completed;
        },
    },

    actions: {
        setMenu({ commit }, payload) {
            const menu = payload?.menu ?? payload;
            const courseId = payload?.courseId ?? null;

            commit('SET_MENU', { menu, courseId });

            commit('UPDATE_STATE', { field: 'hidden', value: false });
        },

        /**
         * Record the course whose menu the page asked for last, so a slower response for a
         * course the user has left can't replace it.
         */
        requestMenuForCourse({ commit }, { courseId, requestId = null }) {
            commit('UPDATE_STATE', { field: 'requestedMenuCourseId', value: Number(courseId) });
            commit('UPDATE_STATE', { field: 'requestedMenuRequestId', value: requestId });
        },

        /**
         * A course menu request failed or was canceled. If that course's menu is still the one
         * the page waits for, show an empty menu for it instead of the previous course's menu.
         */
        dropMenuRequest({ commit, state }, { courseId, requestId = null }) {
            const requestedCourseId = Number(courseId);

            if (state.requestedMenuCourseId !== requestedCourseId || state.menuCourseId === requestedCourseId) {
                return;
            }

            if (requestId != null && requestId !== state.requestedMenuRequestId) {
                return;
            }

            commit('SET_MENU', { menu: defaultMenu, courseId: requestedCourseId });
        },

        setMenuForCourse({ dispatch, state }, data) {
            // Handle different data structures:
            // - Legacy: data.meta.menu
            // - New menu endpoint: data.data.menu (axios response) or data.menu (direct menu data)
            // - MenuService: { data: axiosResponse, courseId }
            let menuData;
            let courseId = null;

            if (data?.courseId != null) {
                courseId = data.courseId;
            }

            const menuPayload = data?.data ?? data;

            if (menuPayload?.data?.menu) {
                // New menu endpoint format (axios response)
                menuData = menuPayload.data.menu;
            } else if (menuPayload?.meta?.menu) {
                // Legacy format
                menuData = menuPayload.meta.menu;
            } else if (menuPayload?.menu) {
                // Direct menu data
                menuData = menuPayload.menu;
            } else {
                // Fallback - assume data is the menu itself
                menuData = menuPayload;
            }

            if (
                courseId != null &&
                state.requestedMenuCourseId != null &&
                Number(courseId) !== state.requestedMenuCourseId
            ) {
                return undefined;
            }

            if (data?.requestId != null && data.requestId !== state.requestedMenuRequestId) {
                return undefined;
            }

            return dispatch('setMenu', { menu: menuData, courseId });
        },

        setStaticMenu({ commit }, { menu = defaultMenu, sidebar }) {
            commit('UPDATE_STATE', { field: 'staticMenu', value: menu });
            commit('UPDATE_STATE', { field: 'hidden', value: false });
            commit('UPDATE_STATE', { field: 'sidebar', value: sidebar });
        },

        hide({ commit }) {
            commit('UPDATE_STATE', { field: 'hidden', value: true });
        },

        show({ commit }) {
            commit('UPDATE_STATE', { field: 'hidden', value: false });
        },

        setSidebar({ commit }, sidebar) {
            commit('UPDATE_STATE', { field: 'sidebar', value: sidebar });
            commit('UPDATE_STATE', { field: 'hidden', value: false });
        },

        showSidebarExpanded({ commit }, route) {
            commit('UPDATE_STATE', { field: 'sidebarExpandedRoute', value: route });
        },

        toggleMobileNav({ commit, state }) {
            commit('UPDATE_STATE', { field: 'mobile_nav_open', value: !state.mobile_nav_open });
        },

        reset({ commit }) {
            commit('SET_MENU', { menu: defaultMenu, courseId: null });

            commit('UPDATE_STATE', { field: 'hidden', value: true });
            commit('UPDATE_STATE', { field: 'staticMenu', value: null });
            commit('UPDATE_STATE', { field: 'sidebar', value: null });
            commit('UPDATE_STATE', { field: 'menuCourseId', value: null });
            commit('UPDATE_STATE', { field: 'requestedMenuCourseId', value: null });
            commit('UPDATE_STATE', { field: 'requestedMenuRequestId', value: null });
        },
    },

    mutations: {
        SET_MENU(state, payload) {
            const menu = payload?.menu ?? payload;
            const courseId = payload?.courseId ?? null;

            if (!menu) {
                console.warn('SET_MENU called with null/undefined menu data');
                return;
            }

            state.dynamicMenu.primary = menu.primary || [];
            state.dynamicMenu.curriculum.misc_pre = menu.curriculum?.misc_pre || [];
            state.dynamicMenu.curriculum.weeks.past = menu.curriculum?.weeks?.past || [];
            state.dynamicMenu.curriculum.weeks.present = menu.curriculum?.weeks?.present || [];
            state.dynamicMenu.curriculum.weeks.future = menu.curriculum?.weeks?.future || [];
            state.dynamicMenu.curriculum.misc_post = menu.curriculum?.misc_post || [];
            state.dynamicMenu.meta = menu.meta || [];
            state.menuCourseId = courseId != null ? Number(courseId) : null;
        },

        UPDATE_STATE(state, { field, value }) {
            if (Object.hasOwn(state, field)) {
                state[field] = value;
            }
        },

        MARK_ACTIVITY_COMPLETED(state, { activityId, completed }) {
            const setCompleted = items => {
                if (!Array.isArray(items)) return;

                items.forEach(item => {
                    if (item && typeof item === 'object') {
                        if (item.type === 'activity' && Number(item.id) === Number(activityId)) {
                            item.completed = completed;
                        }

                        if (Array.isArray(item.items)) setCompleted(item.items);
                    }
                });
            };

            const menu = state.dynamicMenu;
            setCompleted(menu.primary);
            setCompleted(menu.curriculum?.misc_pre);
            setCompleted(menu.curriculum?.weeks?.past);
            setCompleted(menu.curriculum?.weeks?.present);
            setCompleted(menu.curriculum?.weeks?.future);
            setCompleted(menu.curriculum?.misc_post);
            setCompleted(menu.meta);

            // After updating the activity, recompute completion flags for parent sections/weeks
            const recomputeSectionCompletion = items => {
                if (!Array.isArray(items)) return false;

                let allCompleted = true;

                items.forEach(item => {
                    if (!item || typeof item !== 'object') return;

                    if (Array.isArray(item.items) && item.items.length) {
                        const childrenAllDone = recomputeSectionCompletion(item.items);

                        if (item.type === 'section') {
                            item.completed = childrenAllDone;
                        }

                        allCompleted = allCompleted && (item.type === 'activity' ? !!item.completed : childrenAllDone);
                    } else if (item.type === 'activity') {
                        allCompleted = allCompleted && !!item.completed;
                    } else if (item.type === 'section') {
                        // Sections with no children are not considered complete
                        item.completed = false;
                        allCompleted = false;
                    }
                });

                return items.length > 0 ? allCompleted : false;
            };

            recomputeSectionCompletion(menu.primary);
            recomputeSectionCompletion(menu.curriculum?.misc_pre);
            recomputeSectionCompletion(menu.curriculum?.weeks?.past);
            recomputeSectionCompletion(menu.curriculum?.weeks?.present);
            recomputeSectionCompletion(menu.curriculum?.weeks?.future);
            recomputeSectionCompletion(menu.curriculum?.misc_post);
            recomputeSectionCompletion(menu.meta);

            // Force reactivity for consumers that depend on the menu root object
            state.dynamicMenu = { ...state.dynamicMenu };
        },
    },
};
